/* eslint-disable indent */
import {
  defineComponent,
  provide,
  InjectionKey,
  Ref,
  inject,
  toRefs,
  ref,
  onBeforeUnmount,
  PropType,
  h,
  toRef,
  ExtractPropTypes,
  computed
} from "vue";
import { FragmentComponent, useField, useFieldSchema } from "@formily/vue";
import { isValid, uid, clone } from "@formily/shared";
import { ArrayField } from "@formily/core";
import type { Action, ButtonProps } from "element-plus";
import {
  ElButton,
  ElPopconfirm,
  popconfirmProps,
  ElMessageBoxOptions,
  ElMessageBox
} from "element-plus";
import type { Schema } from "@formily/json-schema";
import { Delete, ArrowDown, ArrowUp, Rank, Plus } from "@element-plus/icons-vue";

import { stylePrefix } from "../../__builtins__/configs";
import { composeExport, resolveComponent } from "../../__builtins__/shared";
import { isPromise } from "element-plus/es/utils/index";
import { omit } from "lodash-es";

export interface IArrayBaseAdditionProps extends ButtonProps {
  title?: string;
  method?: "push" | "unshift";
  defaultValue?: any;
}

export type ArrayBaseMixins = {
  Addition?: typeof ArrayBaseAddition;
  Remove?: typeof ArrayBaseRemove;
  MoveUp?: typeof ArrayBaseMoveUp;
  MoveDown?: typeof ArrayBaseMoveDown;
  SortHandle?: typeof ArrayBaseSortHandle;
  Index?: typeof ArrayBaseIndex;
  useArray?: typeof useArray;
  useIndex?: typeof useIndex;
  useRecord?: typeof useRecord;
};

export interface IArrayBaseProps {
  disabled?: boolean;
  keyMap?: WeakMap<Record<string, unknown>, string> | string[] | null;
}

export interface IArrayBaseItemProps {
  index: number;
  record: any;
}

export interface IArrayBaseContext {
  field: Ref<ArrayField>;
  schema: Ref<Schema>;
  props: IArrayBaseProps;
  attrs: {
    [key in string]?: any;
  };
  keyMap?: WeakMap<Record<string, unknown>, string> | string[] | null;
}

const ArrayBaseSymbol: InjectionKey<IArrayBaseContext> = Symbol("ArrayBaseContext");
const ItemSymbol: InjectionKey<IArrayBaseItemProps> = Symbol("ItemContext");

const useArray = () => {
  return inject(ArrayBaseSymbol, null);
};

const useIndex = (index?: number) => {
  const { index: indexRef } = toRefs(inject(ItemSymbol) as IArrayBaseItemProps);
  return indexRef ?? ref(index);
};

const useRecord = (record?: number) => {
  const { record: recordRef } = toRefs(inject(ItemSymbol) as IArrayBaseItemProps);
  return recordRef.value ?? ref(record);
};

const useItemScope = () => {
  return toRef(inject(ItemSymbol) as IArrayBaseItemProps);
};

// const use

const isObjectValue: (schema: Schema) => boolean = (schema: Schema) => {
  if (Array.isArray(schema?.items)) return isObjectValue(schema.items[0]);

  if (schema?.items?.type === "array" || schema?.items?.type === "object") {
    return true;
  }
  return false;
};

const useKey = (schema: Schema) => {
  const isObject = isObjectValue(schema);
  let keyMap: WeakMap<Record<string, unknown>, string> | string[] | null = null;

  if (isObject) {
    keyMap = new WeakMap();
  } else {
    keyMap = [];
  }

  onBeforeUnmount(() => {
    keyMap = null;
  });

  return {
    keyMap,
    getKey: (record: any, index: number) => {
      if (keyMap instanceof WeakMap) {
        if (!keyMap.has(record)) {
          keyMap.set(record, uid());
        }
        return `${keyMap.get(record)}-${index}`;
      }

      if (keyMap && !keyMap[index]) {
        keyMap[index] = uid();
      }
      return keyMap ? `${keyMap[index]}-${index}` : undefined;
    }
  };
};

const getDefaultValue = (defaultValue: any, schema: Schema): any => {
  if (isValid(defaultValue)) return clone(defaultValue);
  if (Array.isArray(schema?.items)) return getDefaultValue(defaultValue, schema.items[0]);
  if (schema?.items?.type === "array") return [];
  if (schema?.items?.type === "boolean") return true;
  if (schema?.items?.type === "date") return "";
  if (schema?.items?.type === "datetime") return "";
  if (schema?.items?.type === "number") return 0;
  if (schema?.items?.type === "object") return {};
  if (schema?.items?.type === "string") return "";
  return null;
};

const ArrayBaseInner = defineComponent({
  name: "ArrayBase",
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    keyMap: {
      type: [WeakMap, Array] as PropType<WeakMap<Record<string, unknown>, string> | string[]>
    }
  },
  setup(props, { slots, attrs }) {
    const field = useField<ArrayField>();
    const schema = useFieldSchema();

    provide(ArrayBaseSymbol, {
      field,
      schema,
      props,
      attrs,
      keyMap: props.keyMap
    });
    return () => {
      return h(FragmentComponent, {}, slots);
    };
  }
});

const ArrayBaseButton = defineComponent({
  name: "ArrayBaseButton",
  props: {
    ...ElButton.props,
    popconfirmProps: {
      type: Object as PropType<ExtractPropTypes<typeof popconfirmProps>>
    },
    messageBoxOptions: {
      type: Object as PropType<ElMessageBoxOptions>
    },
    title: {
      type: String
    },
    link: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: "small"
    },
    onClick: {
      type: Function
    }
  },
  emits: ["click"],
  setup(props, { attrs }) {
    const scope = useItemScope();
    const field = useField();
    const base = useArray();
    const schema = useFieldSchema();
    const buttonProps = omit(props, ["popconfirmProps", "messageBoxOptions"]);
    const popconfirmProps = computed(() => props.popconfirmProps);
    const messageBoxOptions = computed(() => props.messageBoxOptions);
    const loading = ref(false);
    return () => {
      if (base?.field.value.pattern !== "editable") return null;
      const ButtonNode = h(
        ElButton,
        {
          ...buttonProps,
          ...attrs,
          type: buttonProps.type || "primary",
          loading: loading.value,
          onClick: async (e: MouseEvent) => {
            e.stopPropagation();
            if (messageBoxOptions.value) {
              ElMessageBox.confirm("", {
                type: "warning",
                ...messageBoxOptions.value
              })
                .then(async () => {
                  loading.value = true;
                  await messageBoxOptions.value?.onConfirm?.(e, scope.value);
                  loading.value = false;
                })
                .catch(async (action: Action) => {
                  loading.value = true;
                  await messageBoxOptions.value?.onCancel?.(e, scope.value, action);
                  loading.value = false;
                });
            } else {
              if (typeof props.onClick === "function") {
                loading.value = true;
                await props.onClick(e, scope.value);
                loading.value = false;
              }
            }
          }
        },
        {
          default: () => [
            resolveComponent(schema.value["x-render"]?.(scope.value)) ||
              field.value.title ||
              props.title
          ]
        }
      );
      if (popconfirmProps.value) {
        return h(
          ElPopconfirm,
          {
            ...popconfirmProps.value,
            onCancel: async (e: any) => {
              loading.value = true;
              await popconfirmProps.value?.onCancel?.(e, scope.value);
              loading.value = false;
            },
            onConfirm: async (e: any) => {
              loading.value = true;
              popconfirmProps.value?.onConfirm?.(e, scope.value);
              loading.value = false;
            }
          },
          {
            reference: () => ButtonNode
          }
        );
      }
      return ButtonNode;
    };
  }
});

const ArrayBaseItem = defineComponent({
  name: "ArrayBaseItem",
  props: ["index", "record"],
  setup(props: IArrayBaseItemProps, { slots }) {
    provide(ItemSymbol, props);
    return () => {
      return h(FragmentComponent, {}, slots);
    };
  }
});

const ArrayBaseSortHandle = defineComponent({
  name: "ArrayBaseSortHandle",
  props: ["index"],
  setup(props, { attrs }) {
    const array = useArray();
    const prefixCls = `${stylePrefix}-array-base`;

    return () => {
      if (!array) return null;
      if (array.field.value?.pattern !== "editable") return null;

      return h(
        ElButton,
        {
          directives: [{ name: "handle" }],
          size: "small",
          type: "primary",
          text: true,
          icon: Rank,
          ...attrs,
          class: [`${prefixCls}-sort-handle`].concat(attrs.class as any)
        },
        {}
      );
    };
  }
});

const ArrayBaseIndex = defineComponent({
  name: "ArrayBaseIndex",
  setup(props, { attrs }) {
    const index = useIndex();
    const prefixCls = `${stylePrefix}-array-base`;
    return () => {
      const { class: className, ...other } = attrs;
      return h(
        "span",
        {
          class: [`${prefixCls}-index`, className],
          ...other
        },
        {
          default: () => [`${index.value + 1}`]
        }
      );
    };
  }
});

const ArrayBaseAddition = defineComponent({
  name: "ArrayBaseAddition",
  props: ["method", "defaultValue", "title"],
  setup(props, { attrs }) {
    const self = useField();
    const array = useArray();
    const prefixCls = `${stylePrefix}-array-base`;
    return () => {
      if (!array) return null;
      if (array?.field.value.pattern !== "editable") return null;
      const { class: className, ...other } = attrs;
      return h(
        ElButton,
        {
          class: [`${prefixCls}-addition`, className],
          icon: Plus,
          ...other,
          ...props,
          onClick: (e) => {
            if (array.props?.disabled) return;
            const defaultValue = getDefaultValue(props.defaultValue, array?.schema.value);
            if (props.method === "unshift") {
              array?.field?.value.unshift(defaultValue);
              array.attrs?.add?.(0);
            } else {
              array?.field?.value.push(defaultValue);
              array.attrs?.add?.(array?.field?.value?.value?.length - 1);
            }
            if (typeof attrs.onClick === "function") {
              attrs.onClick(e);
            }
          }
        },
        {
          default: () => [self.value.title || props.title]
        }
      );
    };
  }
});

const ArrayBaseRemove = defineComponent({
  name: "ArrayBaseRemove",
  props: {
    ...ArrayBaseButton.props,
    index: {
      type: Number
    },
    onClick: {
      type: Function
    }
  },
  setup(props, { attrs }) {
    const indexRef = useIndex(props.index);
    const self = useField();
    const base = useArray();
    const prefixCls = `${stylePrefix}-array-base`;

    const remove = async (e: MouseEvent) => {
      e.stopPropagation();
      if (typeof props.onClick === "function") {
        const fn = await props.onClick(e);
        if (isPromise(fn)) {
          await fn;
        }
      }
      base?.field.value.remove(indexRef.value as number);
      base?.attrs?.remove?.(indexRef.value as number);
    };

    const popconfirmProps = {
      title: "是否该删除数据",
      ...props.popconfirmProps,
      onConfirm: async (...args: any) => {
        await props.popconfirmProps?.onConfirm?.(...args);
        base?.field.value.remove(indexRef.value as number);
        base?.attrs?.remove?.(indexRef.value as number);
      }
    };

    const messageBoxOptions = {
      message: "是否该删除数据",
      ...props.messageBoxOptions,
      onConfirm: async (...args: any) => {
        await props.messageBoxOptions?.onConfirm?.(...args);
        base?.field.value.remove(indexRef.value as number);
        base?.attrs?.remove?.(indexRef.value as number);
      }
    };

    return () => {
      if (base?.field.value.pattern !== "editable") return null;
      const { class: className, ...other } = attrs;
      return h(
        ArrayBaseButton,
        {
          class: [`${prefixCls}-remove`, className],
          link: true,
          size: "small",
          icon: Delete,
          ...props,
          ...other,
          type: props.type ? props.type : "danger",
          onClick: props.popconfirmProps || props.messageBoxOptions ? () => {} : remove,
          popconfirmProps: props.popconfirmProps ? popconfirmProps : null,
          messageBoxOptions: props.messageBoxOptions ? messageBoxOptions : null
        },
        {
          default: () => [self.value.title || props.title]
        }
      );
    };
  }
});

const ArrayBaseMoveDown = defineComponent({
  name: "ArrayBaseMoveDown",
  props: ["index", "title"],
  setup(props, { attrs }) {
    const indexRef = useIndex(props.index);
    const self = useField();
    const base = useArray();
    const prefixCls = `${stylePrefix}-array-base`;
    return () => {
      if (base?.field.value.pattern !== "editable") return null;

      const { class: className, ...other } = attrs;
      return h(
        ElButton,
        {
          class: [`${prefixCls}-move-down`, className],
          size: "small",
          type: "primary",
          link: true,
          icon: ArrowDown,
          ...other,
          onClick: async (e: MouseEvent) => {
            e.stopPropagation();

            if (typeof attrs.onClick === "function") {
              const fn = attrs.onClick(e);
              if (isPromise(fn)) {
                await fn;
              }
            }

            if (Array.isArray(base?.keyMap)) {
              base.keyMap.splice(indexRef.value + 1, 0, base.keyMap.splice(indexRef.value, 1)[0]);
            }

            base?.field.value.moveDown(indexRef.value as number);
            base?.attrs?.moveDown?.(indexRef.value as number);
          }
        },
        {
          default: () => [self.value.title || props.title]
        }
      );
    };
  }
});

const ArrayBaseMoveUp = defineComponent({
  name: "ArrayBaseMoveUp",
  props: ["index", "title"],
  setup(props, { attrs }) {
    const indexRef = useIndex(props.index);
    const self = useField();
    const base = useArray();
    const prefixCls = `${stylePrefix}-array-base`;
    return () => {
      if (base?.field.value.pattern !== "editable") return null;
      const { class: className, ...other } = attrs;
      return h(
        ElButton,
        {
          class: [`${prefixCls}-move-up`, className],
          size: "small",
          type: "primary",
          link: true,
          icon: ArrowUp,
          ...other,
          onClick: async (e: MouseEvent) => {
            e.stopPropagation();
            if (typeof attrs.onClick === "function") {
              const fn = attrs.onClick(e);
              if (isPromise(fn)) {
                await fn;
              }
            }
            if (Array.isArray(base?.keyMap)) {
              base.keyMap.splice(indexRef.value - 1, 0, base.keyMap.splice(indexRef.value, 1)[0]);
            }

            base?.field.value.moveUp(indexRef.value as number);
            base?.attrs?.moveUp?.(indexRef.value as number);
          }
        },
        {
          default: () => [self.value.title || props.title]
        }
      );
    };
  }
});

const ArrayBaseButtonGroup = defineComponent({
  name: "ArrayBaseButtonGroup",
  inheritAttrs: false,
  props: ["index"],
  setup(props) {
    const indexRef = useIndex(props.index);
    const record = useRecord();
    const schema = useFieldSchema();
    const base = useArray();
    return () => {
      if (base?.field.value.pattern !== "editable") return null;
      return h(
        FragmentComponent,
        {},
        {
          default: () =>
            resolveComponent(schema.value["x-render"]?.({ row: record, $index: indexRef.value }))
        }
      );
    };
  }
});

export const ArrayBase = composeExport(ArrayBaseInner, {
  Index: ArrayBaseIndex,
  Item: ArrayBaseItem,
  SortHandle: ArrayBaseSortHandle,
  Addition: ArrayBaseAddition,
  Remove: ArrayBaseRemove,
  MoveDown: ArrayBaseMoveDown,
  MoveUp: ArrayBaseMoveUp,
  Button: ArrayBaseButton,
  ButtonGroup: ArrayBaseButtonGroup,
  useArray: useArray,
  useIndex: useIndex,
  useKey: useKey,
  useRecord: useRecord
});
